import { useCallback, useState } from 'react';
import { translations, defaultLocale, type Locale } from './locales';
import { IntlOptions } from './types';

/**
 * Função de internacionalização que retorna a tradução ou o próprio key
 * 
 * @param key - Chave da tradução (pode ser nested: 'common.save')
 * @param options - Opções de internacionalização
 * @returns String traduzida ou o próprio key se não encontrado
 * 
 * @example
 * intl('panelTitle') // "Painel do Assessor"
 * intl('common.save') // "Salvar"
 * intl('chave.que.nao.existe') // "chave.que.nao.existe"
 * intl('welcomeText', { values: { name: 'João' } }) // "Bem vindo de volta João"
 */
export function intl(key: string, options: IntlOptions = {}): string {
  const {
    locale = defaultLocale,
    fallback,
    values = {}
  } = options;

  // Obtém as traduções para o locale selecionado
  const localeTranslations = translations[locale];

  // Busca a tradução (suporte para keys nested)
  const translation = getNestedValue(localeTranslations, key);

  if (translation !== undefined && typeof translation === 'string') {
    return replacePlaceholders(translation, values);
  }

  // Se não encontrou e tem fallback, usa o fallback
  if (fallback !== undefined) {
    return replacePlaceholders(fallback, values);
  }

  // Retorna o próprio key como fallback
  return key;
}

/**
 * Obtém valor nested de um objeto baseado em path string
 * 
 * @example
 * getNestedValue({ common: { save: 'Salvar' } }, 'common.save') // 'Salvar'
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = current[key];
  }

  return current;
}

/**
 * Substitui placeholders em strings
 * 
 * @example
 * replacePlaceholders('Bem vindo {name}', { name: 'João' }) // 'Bem vindo João'
 */
function replacePlaceholders(text: string, values: Record<string, any>): string {
  return text.replace(/{(\w+)}/g, (match, key) => {
    return values[key] !== undefined ? String(values[key]) : match;
  });
}

/**
 * Hook para React (opcional)
 */
export function useIntl() {
  // Em uma aplicação real, você pegaria o locale de um context ou store
  const [currentLocale, setCurrentLocale] = useState<Locale>(defaultLocale);

  const t = useCallback((key: string, options?: Omit<IntlOptions, 'locale'>) => {
    return intl(key, { ...options, locale: currentLocale });
  }, [currentLocale]);

  return {
    t,
    currentLocale,
    setCurrentLocale,
  };
}

// Exportação padrão para uso conveniente
export default intl;